import { Radio } from 'antd';
import classNames from 'classnames';
import styles from './index.module.scss';
import { ModalWrap } from '../../../components/modal-wrap';
import getFormatMessage, {
  getLanguage,
} from '../../../locales/get-format-message';
import { DeployArea, RbacRoleObjectKey } from '../../../model';
import {
  CommonStatusStep,
  IAPICompanyFullInfo,
  StudioCertifiedType,
} from '../../../model/api-modules';
import {
  IMidasAccountInfo,
  MidasAccountStatus,
} from '../../../model/billing-center';
import { PermissionCheck } from '../../component/permission-check';
import bookIcon from '../../../assets/svg/book.svg';
import { getConfig } from '../../../config';
import arrowIcon from '../../../assets/svg/arrow-right-up.svg';
import { LanguageType } from '../../../lib';
import { CommonButton, MainButton } from '../../../components/button/main';
import { CommonPrompt } from '../../../components/prompt';
import { useState } from 'react';

export const renderAuthButton = (
  companyInfo: IAPICompanyFullInfo | null | undefined,
  accountInfo: IMidasAccountInfo | null | undefined,
  certType: StudioCertifiedType | undefined,
  onClick: () => void,
  onChangeCertType: (val: StudioCertifiedType) => void
) => {
  // 接口暂时没返回数据
  if (companyInfo === undefined) {
    return null;
  }

  const getButton = () => {
    const text =
      companyInfo?.Status === CommonStatusStep.Reject
        ? getFormatMessage('company_re_auth')
        : getFormatMessage('company_start_auth');
    const handleClick = () => {
      if (!companyInfo) {
        onClick();
        return;
      }
      showCertTypeModal(companyInfo?.lastCertifiedType, (val) => {
        onChangeCertType(val);
        setTimeout(onClick, 100);
      });
    };
    return (
      <MainButton onClick={handleClick} disabled={!companyInfo && !certType}>
        {text}
      </MainButton>
    );
  };

  // 工作室驳回状态处理
  if (
    !companyInfo ||
    (!!companyInfo?.Status &&
      ![CommonStatusStep.Pending, CommonStatusStep.Resolve].includes(
        companyInfo.Status
      ))
  ) {
    const links =
      getConfig().documentConfig.consoleHelperLinks[
        getLanguage || LanguageType.zh_CN
      ];
    const companyHelpLink = links.find(
      (item) => item.label === getFormatMessage('how_company_auth')
    );

    return (
      <div className={styles.titleRight}>
        {!!companyHelpLink && (
          <div className={styles.docLink}>
            <img src={bookIcon} />
            <a onClick={() => window.open(companyHelpLink.url, '_blank')}>
              {getFormatMessage('how_studio_auth')}
            </a>
            <img src={arrowIcon} />
          </div>
        )}
        <PermissionCheck
          permissionKey={RbacRoleObjectKey.StudioAuthentication}
          tipsPlacement="topLeft"
        >
          {getButton()}
        </PermissionCheck>
      </div>
    );
  }

  // 通过状态处理
  const isAccountOverdue =
    accountInfo?.account_status === MidasAccountStatus.OVERDUE;
  if (companyInfo?.Status === CommonStatusStep.Resolve) {
    return (
      <div className={styles.titleRight}>
        <PermissionCheck
          permissionKey={RbacRoleObjectKey.StudioAuthentication}
          tipsPlacement="topLeft"
          otherTips={
            isAccountOverdue
              ? getFormatMessage('company_status_error_warn')
              : ''
          }
        >
          <CommonButton
            className={classNames(styles.modifyAuthBtn)}
            disabled={isAccountOverdue}
            onClick={() => {
              showCertTypeModal(companyInfo.certifiedType, (val) => {
                onChangeCertType(val);
                setTimeout(onClick, 100);
              });
            }}
          >
            {getFormatMessage('company_modify_auth')}
          </CommonButton>
        </PermissionCheck>
      </div>
    );
  }
};

export const showCertTypeModal = (
  initCertType: StudioCertifiedType | undefined,
  onChange: (val: StudioCertifiedType) => void
) => {
  const certOptions = getCertTypeOptions();
  let certType: StudioCertifiedType | undefined =
    certOptions?.length === 1 ? certOptions[0].value : initCertType;
  ModalWrap.show({
    title: getFormatMessage('studio_auth_type'),
    children: (
      <CertTypeChooser
        defaultValue={initCertType}
        onChange={(val) => (certType = val)}
      />
    ),
    oKText: getFormatMessage('confirm'),
    cancelText: getFormatMessage('cancel'),
    onOk: async () => {
      if (certType) {
        onChange(certType);
        return true;
      }
      return false;
    },
  });
};

export const getCertInfoText = (certType?: StudioCertifiedType) => {
  if (!certType) {
    return getFormatMessage('company_auth_desc');
  }
  if (certType === StudioCertifiedType.Company) {
    return getFormatMessage('company_auth_external_desc');
  }
  return getFormatMessage('company_auth_internal_desc');
};

const CertTypeChooser = (props: {
  defaultValue?: StudioCertifiedType;
  onChange: (val: StudioCertifiedType) => void;
}) => {
  const { defaultValue, onChange } = props;
  const [value, setValue] = useState<StudioCertifiedType | undefined>(
    defaultValue
  );

  return (
    <div className={styles.certTypeChooser}>
      <label>{getFormatMessage('studio_auth_choose_type')}</label>
      <div>
        <Radio.Group
          defaultValue={defaultValue}
          options={getCertTypeOptions()}
          onChange={(ev) => {
            setValue(ev.target.value);
            onChange(ev.target.value);
          }}
        />
      </div>
      <CommonPrompt type="warn">
        <span
          dangerouslySetInnerHTML={{ __html: getCertInfoText(value) }}
        ></span>
      </CommonPrompt>
    </div>
  );
};

export const getCertTypeOptions = () => {
  const internalOption = {
    label: getFormatMessage('studio_auth_internal'),
    value: StudioCertifiedType.InternalKpiCode,
  };

  const cooperOption = {
    label: getFormatMessage('studio_auth_cooper'),
    value: StudioCertifiedType.Company,
  };
  if (getConfig().deployArea === DeployArea.woa) {
    return [internalOption];
  }
  if (getConfig().deployArea === DeployArea.singapore) {
    return [internalOption];
  }

  return [internalOption, cooperOption];
};
